import React, { useState, useEffect, useCallback, useRef, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X, Trash2 } from 'lucide-react';
import { listUsers, fetchUserMeta, createUser, updateUser, cancelUser } from '../../services/userAdminApi';
import { fuzzyFilter } from '../../utils/fuzzy';
import { Toggle, Pagination, ConfirmTrashModal } from '../shared';
import { useAuth } from '../../context/AuthContext';

const Spinner = () => (
  <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

// Comptes par page de la liste. Doit rester aligné sur le defaut du serveur
// (UserAdminController::DEFAULT_PAGE_SIZE) : c'est lui qui borne et qui compte.
const PAGE_SIZE = 25;

const EMPTY_FORM = {
  name: '',
  mail: '',
  passwordMode: 'generate', // 'generate' | 'manual'
  password: '',
  roles: [],
  // Project memberships: [{ projectId: '', roles: [] }, ...] — multiple projects,
  // multiple group roles each.
  memberships: [],
};

/**
 * TagMultiSelect — liste à choix multiple « tags » : les valeurs retenues sont des
 * chips retirables DANS le champ, et la saisie autocomplète la liste restante
 * (recherche tolérante aux accents et aux fautes de frappe).
 *
 * Pourquoi ce composant ici et pas le `FuzzySelectField` partagé : celui-ci est
 * un champ reactive-json — il lit et écrit dans le store via GlobalDataContext,
 * ce que cet écran d'administration n'a pas (React classique branché sur
 * /api/orbit/users). Seul le MOTEUR de recherche est mutualisé (`utils/fuzzy`),
 * pas le câblage des données.
 *
 * Le panneau est portalé sur <body> en position fixe : le corps de la pop-in
 * défile (overflow-y-auto) et découperait une liste positionnée en absolu.
 *
 * ARIA combobox (RGAA 11.x / WCAG 4.1.2) : la saisie porte role="combobox" avec
 * aria-controls + aria-activedescendant sur une role="listbox" de role="option" ;
 * les chips sont des <button> FRÈRES de la saisie — jamais imbriqués dedans, un
 * interactif dans un interactif étant un piège pour les technologies d'assistance.
 * Les options déjà retenues sortent de la liste : elles sont sous les yeux en chips.
 */
const TagMultiSelect = ({
  options,
  value,
  onChange,
  disabled = false,
  inputId,
  describedBy,
  placeholder = 'Rechercher…',
  emptyLabel = 'Aucune valeur disponible.',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [posTick, setPosTick] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const activeOptRef = useRef(null);
  const listboxId = useId();
  const optionId = (i) => `${listboxId}-opt-${i}`;

  // Une valeur inconnue du référentiel reste affichée telle quelle plutôt que de
  // disparaître : l'enregistrement renvoie l'ENSEMBLE des valeurs voulues, en
  // masquer une reviendrait à la retirer à l'insu de l'administrateur.
  const selected = useMemo(
    () => value.map((v) => options.find((o) => o.value === v) || { value: v, label: v }),
    [value, options],
  );
  const available = useMemo(
    () => options.filter((o) => !value.includes(o.value)),
    [options, value],
  );
  const filtered = useMemo(() => fuzzyFilter(query, available), [query, available]);

  useEffect(() => { setActiveIndex(0); }, [query, value]);

  const add = (v) => {
    if (disabled || value.includes(v)) return;
    onChange([...value, v]);
    setQuery('');
    inputRef.current?.focus();
  };
  const remove = (v) => {
    if (disabled) return;
    onChange(value.filter((x) => x !== v));
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (filtered.length === 0) return;
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((i) => (i + delta + filtered.length) % filtered.length);
      return;
    }
    if (e.key === 'Enter') {
      if (open && filtered[activeIndex]) {
        // Sinon la pop-in prendrait la touche pour une validation du formulaire.
        e.preventDefault();
        add(filtered[activeIndex].value);
      }
      return;
    }
    if (e.key === 'Backspace' && query === '' && value.length > 0) {
      remove(value[value.length - 1]);
    }
  };

  // Le panneau suit le champ (position fixe), et bascule au-dessus quand la
  // place manque en dessous.
  const panelStyle = useMemo(() => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect) return { position: 'fixed', visibility: 'hidden' };
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const up = spaceBelow < 200 && spaceAbove > spaceBelow;
    const box = { position: 'fixed', left: Math.round(rect.left), width: Math.round(rect.width), zIndex: 60 };
    return up
      ? { ...box, bottom: Math.round(window.innerHeight - rect.top + gap), maxHeight: Math.max(140, Math.min(300, spaceAbove)) }
      : { ...box, top: Math.round(rect.bottom + gap), maxHeight: Math.max(140, Math.min(300, spaceBelow)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, posTick, selected.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onMove = () => setPosTick((t) => t + 1);
    window.addEventListener('scroll', onMove, true); // capture : les scrolls internes aussi
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  // Échap ferme la LISTE, pas la pop-in. Il faut pour cela devancer l'écoute
  // `document` de la pop-in : un `stopPropagation` depuis le champ ne suffit pas
  // (React délègue ses gestionnaires sur la racine de l'application, et l'écoute
  // de la pop-in est posée plus haut, sur `document`). D'où une écoute en phase
  // de CAPTURE sur `document` : elle passe avant tout le monde.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open) activeOptRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  return (
    <div className="relative">
      <div
        ref={boxRef}
        className={`input input-multi flex flex-wrap items-center gap-1.5 pr-7 focus-within:border-iris-500 focus-within:shadow-[var(--ring-focus)] ${
          disabled ? 'cursor-not-allowed bg-slate-100 opacity-70' : 'cursor-text'
        }`}
        onMouseDown={(e) => {
          if (disabled || e.target.closest('button')) return;
          if (e.target !== inputRef.current) {
            e.preventDefault();
            inputRef.current?.focus();
          }
          setOpen(true);
        }}
      >
        {selected.map((c) => (
          <span key={c.value} className="tag tag-iris">
            {c.label}
            <button
              type="button"
              disabled={disabled}
              onClick={() => remove(c.value)}
              aria-label={`Retirer ${c.label}`}
              className="-mr-1 rounded-full p-0.5 text-iris-500 hover:bg-iris-100 hover:text-iris-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-iris-300 disabled:cursor-not-allowed"
            >
              <X size={12} aria-hidden="true" />
            </button>
          </span>
        ))}
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={open && filtered.length > 0 ? optionId(activeIndex) : undefined}
          aria-describedby={describedBy}
          disabled={disabled}
          value={query}
          placeholder={selected.length > 0 ? 'Ajouter…' : placeholder}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={handleKeyDown}
          className="min-w-[5rem] flex-1 border-0 bg-transparent p-0 text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
        />
      </div>
      {/* Hors du flux : dans le flux, le chevron passait à la ligne dès que les
          étiquettes remplissaient la première. Inerte au pointeur — le clic doit
          tomber dans la boîte, qui donne le focus à la saisie. */}
      <ChevronDown
        size={16}
        aria-hidden="true"
        className={`pointer-events-none absolute right-2.5 top-[0.6875rem] text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
      />

      {/* Région live persistante : insérée déjà remplie, elle ne serait pas vocalisée. */}
      <span className="sr-only" role="status" aria-live="polite">
        {open ? `${filtered.length} résultat${filtered.length > 1 ? 's' : ''}` : ''}
      </span>

      {open && createPortal(
        <div
          ref={panelRef}
          style={panelStyle}
          className="flex min-w-[14rem] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          <ul id={listboxId} role="listbox" className="min-h-0 flex-1 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-slate-400">
                {available.length === 0 ? emptyLabel : `Aucun résultat pour «\u00a0${query.trim()}\u00a0»`}
              </li>
            ) : (
              filtered.map((opt, i) => (
                <li
                  key={opt.value}
                  id={optionId(i)}
                  role="option"
                  aria-selected={false}
                  ref={i === activeIndex ? activeOptRef : null}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(opt.value)}
                  className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-sm ${
                    i === activeIndex ? 'bg-iris-50 text-iris-700' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span className="truncate">{opt.label}</span>
                  {i === activeIndex && <Check size={14} aria-hidden="true" className="flex-shrink-0" />}
                </li>
              ))
            )}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
};

/**
 * Raccourci « Tout cocher / Tout décocher » d'une liste de rôles.
 *
 * Posé À CÔTÉ du libellé et non dans la liste déroulante : le geste doit rester
 * atteignable sans rien ouvrir, et il sert aussi aux rôles de groupe d'un projet,
 * qui n'ont pas de liste à ouvrir.
 *
 * « Tout cocher » AJOUTE aux valeurs déjà retenues au lieu de les remplacer : une
 * valeur inconnue du référentiel (rôle supprimé depuis) reste affichée par
 * `TagMultiSelect` — la remplacer la retirerait à l'insu de l'administrateur.
 *
 * Chaque bouton s'éteint quand il n'a plus rien à faire : un contrôle désactivé
 * annonce l'état, là où un bouton inerte laisserait croire à une panne. Le nom
 * accessible reprend le texte visible en tête (WCAG 2.5.3) et le complète par ce
 * sur quoi il porte — deux paires de boutons peuvent cohabiter dans l'écran.
 */
const BulkSelect = ({ allValues, value, onChange, subject, disabled = false }) => {
  const allChecked = allValues.length > 0 && allValues.every((v) => value.includes(v));
  return (
    <span className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={disabled || allChecked}
        aria-label={`Tout cocher ${subject}`}
        onClick={() => onChange([...value, ...allValues.filter((v) => !value.includes(v))])}
      >
        Tout cocher
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={disabled || value.length === 0}
        aria-label={`Tout décocher ${subject}`}
        onClick={() => onChange([])}
      >
        Tout décocher
      </button>
    </span>
  );
};

/**
 * Pop-in d'édition d'un compte : nom, e-mail, activité et rôles globaux.
 *
 * Les rôles passent par un `TagMultiSelect` : chips retirables + autocomplétion.
 * Un `<select multiple>` natif tenait le clavier et le mobile pour rien, mais sa
 * sélection à Ctrl/⌘ + clic ne s'invente pas et se perd d'un clic malheureux —
 * sur une liste de rôles qui grandit, ajouter et retirer devait devenir un geste
 * unitaire, lisible d'un coup d'œil. Le clavier et l'annonce vocale sont donc
 * réimplémentés à la main (motif ARIA combobox), pas abandonnés.
 *
 * L'activité est un interrupteur (`Toggle`, role="switch") : l'état est binaire,
 * il se lit sans ouvrir quoi que ce soit et bascule d'un geste.
 *
 * Le mot de passe n'y figure pas volontairement : il se réinitialise par lien à
 * usage unique, jamais en le lisant ni en l'écrivant depuis un écran.
 *
 * L'enregistrement envoie l'ENSEMBLE des rôles voulus, pas un différentiel :
 * c'est le serveur qui calcule ce qu'il ajoute et retire.
 */
const UserEditModal = ({ form, roles, onField, onRolesChange, onCancel, onSave, saving, error }) => {
  const titleId = 'user-edit-title';
  const firstFieldRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && !saving) onCancel(); };
    document.addEventListener('keydown', handler);
    firstFieldRef.current?.focus();
    return () => document.removeEventListener('keydown', handler);
  }, [saving, onCancel]);

  // `TagMultiSelect` parle en {value,label} ; le méta serveur en {id,label}.
  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: r.id, label: r.label })),
    [roles],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={saving ? undefined : onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-stone-200 overflow-hidden flex flex-col max-h-[88vh]">
        <div className="px-6 pt-5 pb-3 border-b border-gray-100">
          <h3 id={titleId} className="text-base font-semibold text-gray-900">
            Modifier le compte
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Le mot de passe ne se modifie pas ici : il se réinitialise par lien à usage unique.
          </p>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="field-label mb-1" htmlFor="edit-user-name">Nom d'utilisateur *</label>
              <input
                id="edit-user-name"
                ref={firstFieldRef}
                type="text"
                value={form.name}
                onChange={(e) => onField('name', e.target.value)}
                disabled={saving}
                className="input"
              />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="edit-user-mail">Email *</label>
              <input
                id="edit-user-mail"
                type="email"
                value={form.mail}
                onChange={(e) => onField('mail', e.target.value)}
                disabled={saving}
                className="input"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="min-w-0">
              <span className="field-label">Compte actif</span>
              <p className="mt-0.5 text-xs text-slate-500">
                {form.status
                  ? "L'utilisateur peut se connecter."
                  : "Bloqué : l'utilisateur ne peut plus se connecter."}
              </p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              <span className={`badge badge-sm ${form.status ? 'badge-success' : 'badge-neutral'}`}>
                {form.status ? 'Actif' : 'Bloqué'}
              </span>
              <Toggle
                checked={form.status}
                onChange={() => onField('status', !form.status)}
                disabled={saving}
                label="Compte actif"
              />
            </div>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <label className="field-label mb-0" htmlFor="edit-user-roles">Rôles globaux</label>
              {roles.length > 0 && (
                <BulkSelect
                  allValues={roleOptions.map((o) => o.value)}
                  value={form.roles}
                  onChange={onRolesChange}
                  disabled={saving}
                  subject="les rôles globaux"
                />
              )}
            </div>
            {roles.length === 0 ? (
              <p className="text-xs text-gray-400">Aucun rôle assignable.</p>
            ) : (
              <>
                <TagMultiSelect
                  inputId="edit-user-roles"
                  describedBy="edit-user-roles-help"
                  options={roleOptions}
                  value={form.roles}
                  onChange={onRolesChange}
                  disabled={saving}
                  placeholder="Rechercher un rôle…"
                  emptyLabel="Tous les rôles sont déjà attribués."
                />
                <p id="edit-user-roles-help" className="text-xs text-gray-400 mt-1">
                  Saisissez pour filtrer, Entrée pour ajouter ; la croix d'une étiquette
                  (ou Retour arrière) retire le rôle. Les rôles se cumulent.
                </p>
              </>
            )}
          </div>

          {error && (
            <p role="alert" className="text-sm text-red-600">{error}</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <span className="text-xs text-gray-400">
            {form.roles.length} rôle{form.roles.length > 1 ? 's' : ''} sélectionné{form.roles.length > 1 ? 's' : ''}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onCancel} disabled={saving} className="btn btn-secondary">
              Annuler
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || !form.name.trim() || !form.mail.trim()}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving && <Spinner />}
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Options de suppression d'un compte — le contenu de la pop-in de confirmation.
 *
 * Rien n'y est inventé : les méthodes viennent de `user_cancel_methods()` côté
 * serveur, dans l'ordre de Drupal et avec SES libellés — y compris ceux que les
 * modules installés réécrivent (le module Group y dit ce qu'il advient des
 * projets et des sites possédés par le compte). Un écran qui les reformulerait
 * mentirait le jour où un module change la règle.
 *
 * Les deux cases reprennent celles de /user/{uid}/cancel :
 *  - « Prévenir l'utilisateur » n'apparaît que si le site a activé la
 *    notification de compte annulé (`notifyAvailable`), comme le `#access` du
 *    formulaire du cœur ;
 *  - « Demander confirmation par e-mail » diffère l'annulation : l'utilisateur
 *    reçoit un lien, rien n'est fait tant qu'il n'a pas cliqué.
 *
 * Groupe de boutons radio dans un `<fieldset>`/`<legend>` (RGAA 11.5) : le
 * choix est exclusif et la question doit être annoncée avec chaque option.
 */
const CancelAccountOptions = ({
  methods,
  method,
  onMethod,
  notify,
  onNotify,
  notifyAvailable,
  confirmMail,
  onConfirmMail,
  disabled,
}) => (
  <div className="mt-4 w-full text-left">
    <fieldset>
      {/* Le nom du compte est déjà dans le titre de la pop-in : le répéter ici
          ferait lire deux fois la même chose au lecteur d'écran. */}
      <legend className="field-label mb-2">Que faire de ce compte ?</legend>
      <div className="space-y-2">
        {/* Le chargement des métadonnées a échoué : sans méthode à choisir, il
            n'y a rien à confirmer — la confirmation est désarmée juste en
            dessous plutôt que de laisser partir un défaut invisible. */}
        {methods.length === 0 && (
          <p className="alert alert-danger text-left">
            Les options de suppression n'ont pas pu être chargées. Rechargez la page.
          </p>
        )}
        {methods.map((m) => (
          <label key={m.id} className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="user-cancel-method"
              className="radio mt-0.5"
              value={m.id}
              checked={method === m.id}
              disabled={disabled}
              onChange={() => onMethod(m.id)}
            />
            <span>{m.label}</span>
          </label>
        ))}
      </div>
    </fieldset>

    <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
      {notifyAvailable && (
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="checkbox mt-0.5"
            checked={notify}
            disabled={disabled}
            onChange={(e) => onNotify(e.target.checked)}
          />
          <span>Prévenir l'utilisateur par e-mail</span>
        </label>
      )}
      <label className="flex items-start gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          className="checkbox mt-0.5"
          checked={confirmMail}
          disabled={disabled}
          onChange={(e) => onConfirmMail(e.target.checked)}
        />
        <span>
          Demander confirmation par e-mail
          <span className="field-hint">
            Rien n'est appliqué maintenant : l'utilisateur reçoit un lien et décide lui-même.
          </span>
        </span>
      </label>
    </div>
  </div>
);

/**
 * Configuration → Utilisateurs : list existing accounts and create new ones
 * (global role + optional project membership), mirroring /admin/people.
 */
const UserManagementPanel = ({ showToast }) => {
  // Le compte courant : c'est la seule ligne, avec l'uid 1, sur laquelle le
  // serveur refuse la suppression. Sans lui, l'écran offrirait un geste dont
  // l'utilisateur ne découvre l'impasse qu'après avoir confirmé.
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Terme réellement envoyé au serveur (le champ est débouncé) + page courante.
  // La liste est paginée CÔTÉ SERVEUR : `total`/`pageCount` viennent de lui,
  // `users` ne contient qu'une page.
  const [query, setQuery] = useState('');
  // Rôle global exigé des comptes listés ('' = tous). Filtré CÔTÉ SERVEUR, comme
  // la recherche : la page ne contient que 25 comptes, y trier localement
  // donnerait un décompte et une pagination faux.
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState({
    roles: [],
    projects: [],
    projectRoles: [],
    // Méthodes d'annulation de compte : c'est le serveur qui les tient (Drupal
    // et ses modules), l'écran ne fait que les proposer.
    cancelMethods: [],
    defaultCancelMethod: 'user_cancel_block',
    cancelNotifyAvailable: false,
  });

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [resetLink, setResetLink] = useState(null);
  const [copied, setCopied] = useState(false);

  // Édition d'un compte existant (pop-in) : identité, activité et rôles.
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', mail: '', status: true, roles: [] });
  const [savingRoles, setSavingRoles] = useState(false);
  const [editError, setEditError] = useState('');

  // Suppression d'un compte (pop-in de confirmation) : le compte visé, la
  // méthode Drupal retenue et les deux options du formulaire du cœur.
  const [canceling, setCanceling] = useState(null);
  const [cancelOpts, setCancelOpts] = useState({ method: '', notify: false, confirmMail: false });
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState('');

  // `showToast` est recréé à chaque rendu du parent : le garder en référence
  // rend `loadUsers` stable, sans quoi l'effet ci-dessous rechargerait la page
  // courante à chaque rendu de l'écran de configuration.
  const toastRef = useRef(showToast);
  toastRef.current = showToast;

  const loadUsers = useCallback((q, role, p) => {
    setLoading(true);
    listUsers({ q, role, page: p, pageSize: PAGE_SIZE })
      .then((res) => {
        setUsers(res.users || []);
        setTotal(res.total ?? (res.users?.length || 0));
        setPageCount(res.pageCount ?? 1);
        // Le serveur borne la page demandée (une recherche peut réduire le
        // nombre de pages) : on s'aligne sur celle qu'il a réellement servie,
        // sinon la barre resterait sur une page qui n'existe plus.
        if (res.page && res.page !== p) setPage(res.page);
      })
      .catch((err) => toastRef.current?.(err.message || 'Erreur de chargement', 'error'))
      .finally(() => setLoading(false));
  }, []);

  // Un seul chargement, pour le premier rendu comme pour toute navigation : le
  // terme débouncé, le rôle retenu et la page sont les seules entrées de la liste.
  useEffect(() => { loadUsers(query, roleFilter, page); }, [loadUsers, query, roleFilter, page]);

  useEffect(() => {
    fetchUserMeta()
      // Fusion et non remplacement : une clé que le serveur n'enverrait pas
      // (backend plus ancien) garde sa valeur par défaut au lieu de devenir
      // `undefined` et de casser la pop-in de suppression.
      .then((m) => setMeta((prev) => ({ ...prev, ...m })))
      .catch((err) => showToast?.(err.message || 'Erreur métadonnées', 'error'));
  }, [showToast]);

  // Recherche débouncée. Un nouveau terme REMET LA PAGE À 1 : rester en page 3
  // d'un jeu de résultats devenu plus court n'afficherait rien.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // `TagMultiSelect` parle en {value,label} ; le méta serveur en {id,label}.
  const roleOptions = useMemo(
    () => meta.roles.map((r) => ({ value: r.id, label: r.label })),
    [meta.roles],
  );

  // Rôles globaux du formulaire de création : même liste à choix multiple que la
  // pop-in d'édition (`TagMultiSelect`), qui parle en ENSEMBLE de rôles voulus.
  const setRoles = (next) => setForm((f) => ({ ...f, roles: next }));

  // ── Project membership rows (multiple projects, multiple roles each) ──
  const addMembership = () => setForm((f) => ({
    ...f,
    memberships: [...f.memberships, { projectId: '', roles: [] }],
  }));

  const removeMembership = (idx) => setForm((f) => ({
    ...f,
    memberships: f.memberships.filter((_, i) => i !== idx),
  }));

  const setMembershipProject = (idx, projectId) => setForm((f) => ({
    ...f,
    // Changing the project resets the chosen roles (they belong to a project).
    memberships: f.memberships.map((m, i) => (i === idx ? { projectId, roles: [] } : m)),
  }));

  const toggleMembershipRole = (idx, rid) => setForm((f) => ({
    ...f,
    memberships: f.memberships.map((m, i) => {
      if (i !== idx) return m;
      const roles = m.roles.includes(rid) ? m.roles.filter((r) => r !== rid) : [...m.roles, rid];
      return { ...m, roles };
    }),
  }));

  // Pendant « ensemble » de `toggleMembershipRole` : ce que réclame `BulkSelect`,
  // qui raisonne en liste voulue et non en bascule unitaire.
  const setMembershipRoles = (idx, next) => setForm((f) => ({
    ...f,
    memberships: f.memberships.map((m, i) => (i === idx ? { ...m, roles: next } : m)),
  }));

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setResetLink(null);
    setCopied(false);
    setCreating(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setResetLink(null);
    try {
      const payload = {
        name: form.name.trim(),
        mail: form.mail.trim(),
        generatePassword: form.passwordMode === 'generate',
        password: form.passwordMode === 'manual' ? form.password : undefined,
        roles: form.roles,
        memberships: form.memberships
          .filter((m) => m.projectId)
          .map((m) => ({ projectId: Number(m.projectId), roles: m.roles })),
      };
      const { resetLink: link } = await createUser(payload);
      // Le compte créé est le plus récent (tri `created` DESC) : il ouvre la
      // PREMIÈRE page. On la recharge au lieu de l'injecter dans la page
      // courante, qui déborderait de sa taille et décalerait tout le reste.
      if (page === 1) loadUsers(query, roleFilter, 1); else setPage(1);
      showToast?.('Utilisateur créé', 'success');
      if (link) {
        // Keep the form open to display the one-time link for the admin to copy.
        setResetLink(link);
        setForm((f) => ({ ...EMPTY_FORM, passwordMode: f.passwordMode }));
      } else {
        setCreating(false);
      }
    } catch (err) {
      showToast?.(err.message || 'Erreur lors de la création', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast?.('Copie impossible — sélectionnez le lien manuellement', 'error');
    }
  };

  const roleLabel = (rid) => meta.roles.find((r) => r.id === rid)?.label || rid;

  // ── Édition d'un compte existant ────────────────────────────────────────
  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      name: u.name || '',
      mail: u.mail || '',
      status: !!u.status,
      roles: u.roles || [],
    });
    setEditError('');
  };

  const setEditField = (key, value) => setEditForm((f) => ({ ...f, [key]: value }));

  // Rien à enregistrer si rien n'a bougé : on évite un aller-retour serveur (et
  // un toast) pour une pop-in simplement ouverte puis refermée.
  const editChanged = useMemo(() => {
    if (!editing) return false;
    const beforeRoles = [...(editing.roles || [])].sort();
    const afterRoles = [...editForm.roles].sort();
    return (editing.name || '') !== editForm.name
      || (editing.mail || '') !== editForm.mail
      || !!editing.status !== editForm.status
      || beforeRoles.length !== afterRoles.length
      || beforeRoles.some((r, i) => r !== afterRoles[i]);
  }, [editing, editForm]);

  const saveEdit = async () => {
    if (!editing || savingRoles) return;
    if (!editChanged) { setEditing(null); return; }
    setSavingRoles(true);
    setEditError('');
    try {
      const { user } = await updateUser(editing.uid, {
        name: editForm.name.trim(),
        mail: editForm.mail.trim(),
        status: editForm.status,
        roles: editForm.roles,
      });
      setUsers((prev) => prev.map((u) => (u.uid === user.uid ? user : u)));
      showToast?.('Compte mis à jour', 'success');
      setEditing(null);
    } catch (err) {
      // L'erreur reste DANS la pop-in : le serveur refuse par exemple un e-mail
      // déjà pris, ou qu'on se bloque soi-même, et la corriger suppose de garder
      // la saisie sous les yeux.
      setEditError(err.message || 'Échec de la mise à jour');
    } finally {
      setSavingRoles(false);
    }
  };

  // ── Suppression d'un compte ─────────────────────────────────────────────
  // « Supprimer » à l'écran, `user_cancel()` côté Drupal : la méthode retenue
  // décide si le compte est bloqué ou réellement supprimé, et ce qu'il advient
  // de ses contenus. La liste des méthodes vient du serveur.
  const openCancel = (u) => {
    setCanceling(u);
    setCancelOpts({
      method: meta.defaultCancelMethod || meta.cancelMethods[0]?.id || 'user_cancel_block',
      notify: false,
      confirmMail: false,
    });
    setCancelError('');
  };

  const cancelMethodInfo = useMemo(
    () => meta.cancelMethods.find((m) => m.id === cancelOpts.method) || null,
    [meta.cancelMethods, cancelOpts.method],
  );

  const confirmCancel = async () => {
    // Rien à confirmer tant que le serveur n'a pas dit quelles méthodes il
    // accepte : confirmer enverrait une méthode que personne n'a choisie.
    if (!canceling || cancelBusy || meta.cancelMethods.length === 0) return;
    setCancelBusy(true);
    setCancelError('');
    try {
      const res = await cancelUser(canceling.uid, cancelOpts);
      if (res.status === 'confirmation_sent') {
        showToast?.("Demande de confirmation envoyée à l'utilisateur", 'success');
      } else if (res.status === 'blocked') {
        setUsers((prev) => prev.map((u) => (u.uid === res.user.uid ? res.user : u)));
        showToast?.('Compte bloqué', 'success');
      } else {
        // Une ligne en moins : la page courante peut avoir perdu son dernier
        // compte, et le total comme le nombre de pages ont bougé. On recharge
        // au lieu de retirer la ligne en local — le serveur borne la page.
        loadUsers(query, roleFilter, page);
        showToast?.('Compte supprimé', 'success');
      }
      setCanceling(null);
    } catch (err) {
      // Le refus reste DANS la pop-in : le serveur écarte par exemple le compte
      // principal ou le compte courant, et l'admin doit lire pourquoi.
      setCancelError(err.message || 'Échec de la suppression');
    } finally {
      setCancelBusy(false);
    }
  };

  const manualValid =
    form.passwordMode !== 'manual' || form.password.length >= 8;
  // Every membership row must have a project AND at least one group role.
  const membershipsValid = form.memberships.every((m) => m.projectId && m.roles.length > 0);
  const canSubmit =
    form.name.trim() && form.mail.trim() && manualValid &&
    membershipsValid && !submitting;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Utilisateurs</h3>
          <p className="text-sm text-gray-500 mt-1">Créez des comptes et assignez des rôles</p>
        </div>
        {!creating && (
          <button
            onClick={openCreate}
            className="btn btn-primary flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Nouvel utilisateur
          </button>
        )}
      </div>

      {creating && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl mb-8">
          {resetLink ? (
            <div>
              <div className="flex items-center gap-2 mb-3 text-green-700">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                <span className="font-medium">Compte créé</span>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                Transmettez ce lien de connexion à usage unique à l'utilisateur pour qu'il
                définisse son mot de passe :
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={resetLink}
                  onFocus={(e) => e.target.select()}
                  className="input flex-grow font-mono"
                />
                <button
                  onClick={copyLink}
                  className="btn btn-primary whitespace-nowrap"
                >
                  {copied ? 'Copié !' : 'Copier'}
                </button>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => { setResetLink(null); }}
                  className="btn btn-ghost"
                >
                  Créer un autre utilisateur
                </button>
                <button
                  onClick={() => { setResetLink(null); setCreating(false); }}
                  className="btn btn-secondary"
                >
                  Terminer
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="field-label mb-1">Nom d'utilisateur *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    className="input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="field-label mb-1">Email *</label>
                  <input
                    type="email"
                    value={form.mail}
                    onChange={(e) => set('mail', e.target.value)}
                    className="input"
                  />
                </div>
              </div>

              {/* Password mode */}
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</span>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="passwordMode"
                      checked={form.passwordMode === 'generate'}
                      onChange={() => set('passwordMode', 'generate')}
                      className="radio mt-0.5"
                    />
                    <span>
                      Mot de passe aléatoire + lien de réinitialisation
                      <span className="block text-xs text-gray-400">Le lien à usage unique s'affichera après la création.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="passwordMode"
                      checked={form.passwordMode === 'manual'}
                      onChange={() => set('passwordMode', 'manual')}
                      className="radio mt-0.5"
                    />
                    <span>Définir un mot de passe</span>
                  </label>
                  {form.passwordMode === 'manual' && (
                    <input
                      type="text"
                      value={form.password}
                      onChange={(e) => set('password', e.target.value)}
                      placeholder="Au moins 8 caractères"
                      className="input ml-6"
                      style={{ width: 'calc(100% - 1.5rem)' }}
                    />
                  )}
                </div>
              </div>

              {/* Global roles */}
              <div>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <label className="field-label mb-0" htmlFor="create-user-roles">Rôles globaux</label>
                  {meta.roles.length > 0 && (
                    <BulkSelect
                      allValues={roleOptions.map((o) => o.value)}
                      value={form.roles}
                      onChange={setRoles}
                      disabled={submitting}
                      subject="les rôles globaux"
                    />
                  )}
                </div>
                {meta.roles.length === 0 ? (
                  <p className="text-xs text-gray-400">Aucun rôle assignable.</p>
                ) : (
                  <>
                    <TagMultiSelect
                      inputId="create-user-roles"
                      describedBy="create-user-roles-help"
                      options={roleOptions}
                      value={form.roles}
                      onChange={setRoles}
                      disabled={submitting}
                      placeholder="Rechercher un rôle…"
                      emptyLabel="Tous les rôles sont déjà attribués."
                    />
                    <p id="create-user-roles-help" className="text-xs text-gray-400 mt-1">
                      Saisissez pour filtrer, Entrée pour ajouter ; la croix d'une étiquette
                      (ou Retour arrière) retire le rôle. Les rôles se cumulent.
                    </p>
                  </>
                )}
              </div>

              {/* Project memberships — multiple projects, multiple roles each */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="block text-sm font-medium text-gray-700">Projets &amp; rôles de groupe</span>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Rattachez l'utilisateur à un ou plusieurs projets, avec un ou plusieurs rôles de
                      groupe par projet.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addMembership}
                    className="btn btn-secondary btn-sm flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Ajouter un projet
                  </button>
                </div>

                {form.memberships.length === 0 ? (
                  <p className="text-xs text-gray-400 italic mt-3">Aucun projet — l'utilisateur n'aura aucun accès projet.</p>
                ) : (
                  <div className="space-y-3 mt-3">
                    {form.memberships.map((m, idx) => {
                      // Projects already chosen in other rows are not selectable here.
                      const takenElsewhere = form.memberships
                        .filter((_, i) => i !== idx)
                        .map((x) => String(x.projectId))
                        .filter(Boolean);
                      const projectOptions = meta.projects.filter(
                        (p) => !takenElsewhere.includes(String(p.id)),
                      );
                      return (
                        <div key={idx} className="rounded-md border border-gray-200 bg-white p-3">
                          <div className="flex items-start gap-2">
                            <select
                              value={m.projectId}
                              onChange={(e) => setMembershipProject(idx, e.target.value)}
                              className="select flex-grow"
                            >
                              <option value="">— Choisir un projet —</option>
                              {projectOptions.map((p) => (
                                <option key={p.id} value={p.id}>{p.label}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => removeMembership(idx)}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md"
                              title="Retirer ce projet"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                          </div>

                          {m.projectId && (
                            <div className="mt-3">
                              <div className="mb-1.5 flex items-center justify-between gap-2">
                                <span className="block text-xs font-medium text-gray-500">Rôles de groupe *</span>
                                {meta.projectRoles.length > 0 && (
                                  <BulkSelect
                                    allValues={meta.projectRoles.map((r) => r.id)}
                                    value={m.roles}
                                    onChange={(next) => setMembershipRoles(idx, next)}
                                    disabled={submitting}
                                    subject="les rôles de groupe de ce projet"
                                  />
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {meta.projectRoles.map((r) => (
                                  <button
                                    type="button"
                                    key={r.id}
                                    // Bascule, pas action : sans `aria-pressed`, seule la
                                    // couleur disait qu'un rôle est retenu.
                                    aria-pressed={m.roles.includes(r.id)}
                                    onClick={() => toggleMembershipRole(idx, r.id)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                                      m.roles.includes(r.id)
                                        ? 'bg-iris-600 text-white border-iris-600'
                                        : 'bg-white text-gray-600 border-gray-300 hover:border-iris-300'
                                    }`}
                                  >
                                    {r.label}
                                  </button>
                                ))}
                              </div>
                              {m.roles.length === 0 && (
                                <p className="text-xs text-amber-600 mt-1.5">Sélectionnez au moins un rôle.</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setCreating(false)}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {submitting && <Spinner />}
                  Créer l'utilisateur
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* User list */}
      <div className="max-w-4xl">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou email…"
              aria-label="Rechercher un utilisateur par nom ou e-mail"
              className="input w-72"
            />
            {/* Filtre par rôle global. Comme la recherche, il repart en PAGE 1 :
                rester en page 3 d'un jeu de résultats devenu plus court
                n'afficherait rien. Les options sont celles du serveur
                (`meta.roles`), jamais une liste en dur. */}
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
              aria-label="Filtrer les utilisateurs par rôle"
              className="select w-52"
            >
              <option value="">Tous les rôles</option>
              {meta.roles.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </div>
          {/* Le décompte porte sur le TOTAL filtré, pas sur la page affichée. */}
          <span className="text-sm text-gray-400">
            {total} utilisateur{total > 1 ? 's' : ''}
            {pageCount > 1 && ` — page ${page} sur ${pageCount}`}
          </span>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-gray-400 text-sm">
              <Spinner /> Chargement…
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">Aucun utilisateur</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Nom</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Email</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Rôles</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Statut</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((u) => (
                  <tr key={u.uid} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-900">{u.name}</td>
                    <td className="px-4 py-2 text-gray-500">{u.mail || '—'}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {u.roles.length === 0 ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          u.roles.map((r) => (
                            <span key={r} className="badge badge-primary">
                              {roleLabel(r)}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${u.status ? 'text-green-700' : 'text-gray-400'}`}>
                        <span className={`w-2 h-2 rounded-full ${u.status ? 'bg-green-500' : 'bg-gray-300'}`} />
                        {u.status ? 'Actif' : 'Bloqué'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {u.uid === 1 ? (
                        // Le compte principal est le secours du site : il ne se
                        // modifie pas ici (le serveur le refuse aussi).
                        <span className="text-xs text-gray-300">—</span>
                      ) : (
                        <div className="inline-flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(u)}
                            className="btn btn-ghost btn-sm inline-flex items-center gap-1.5"
                            aria-label={`Modifier le compte de ${u.name}`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                            Modifier
                          </button>
                          {/* Pas sur sa propre ligne : le serveur refuse (on se
                              déconnecterait au premier rechargement), et un
                              bouton qui n'aboutit jamais est un piège. */}
                          {u.uid !== currentUser?.uid && (
                            <button
                              type="button"
                              onClick={() => openCancel(u)}
                              className="btn btn-ghost-danger btn-sm inline-flex items-center gap-1.5"
                              aria-label={`Supprimer le compte de ${u.name}`}
                            >
                              <Trash2 size={14} aria-hidden="true" />
                              Supprimer
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination — le composant se masque lui-même s'il n'y a qu'une page. */}
        <div className="flex justify-center mt-4">
          <Pagination
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            label="Pagination des utilisateurs"
          />
        </div>
      </div>

      {editing && (
        <UserEditModal
          form={editForm}
          roles={meta.roles}
          onField={setEditField}
          onRolesChange={(rs) => setEditField('roles', rs)}
          onCancel={() => setEditing(null)}
          onSave={saveEdit}
          saving={savingRoles}
          error={editError}
        />
      )}

      {/* Suppression : la pop-in de confirmation commune, dont le corps porte
          les options de Drupal. Le libellé du bouton dit ce qui va VRAIMENT se
          passer — bloquer, supprimer, ou seulement demander confirmation. */}
      <ConfirmTrashModal
        isOpen={!!canceling}
        variant="purge"
        title={`Supprimer le compte « ${canceling?.name ?? ''} »`}
        description="Drupal propose plusieurs façons d'en finir avec un compte : elles ne traitent pas ses contenus de la même manière."
        confirmLabel={
          cancelOpts.confirmMail
            ? 'Envoyer la demande'
            : (cancelMethodInfo?.deletesAccount ? 'Supprimer' : 'Bloquer')
        }
        loading={cancelBusy}
        error={cancelError}
        onCancel={() => { if (!cancelBusy) setCanceling(null); }}
        onConfirm={confirmCancel}
      >
        <CancelAccountOptions
          methods={meta.cancelMethods}
          method={cancelOpts.method}
          onMethod={(m) => setCancelOpts((o) => ({ ...o, method: m }))}
          notify={cancelOpts.notify}
          onNotify={(v) => setCancelOpts((o) => ({ ...o, notify: v }))}
          notifyAvailable={meta.cancelNotifyAvailable}
          confirmMail={cancelOpts.confirmMail}
          onConfirmMail={(v) => setCancelOpts((o) => ({ ...o, confirmMail: v }))}
          disabled={cancelBusy}
        />
      </ConfirmTrashModal>
    </div>
  );
};

export default UserManagementPanel;
